import {
  DndContext,
  closestCenter,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import {isFeatureEnabled} from 'feature-flags';
import {Button, useLocalStorage} from 'platform/components';
import {HStack, Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {isNil, sortBy} from 'ramda';

import {
  PresetListItemResponseBody,
  useSourcingGetPresetsListQuery,
  useSourcingReorderPresetsMutation,
} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {handleApiError, SkeletonLoadingBox} from '@dms/shared';

import {Nullish} from 'shared/web';

import {sourcingStorageKey} from '../../../../constants/sourcingStorageKey';
import {useSourcingCurrency} from '../../../../hooks/useSourcingCurrency';
import {useSourcingTargetCountry} from '../../../../hooks/useSourcingTargetCountry';
import {SourcingType, useSourcingType} from '../../../../hooks/useSourcingType';
import {SourcingFilterPresetsControl} from '../../hooks/useSourcingFilterPresets';
import {getReorderedPresetIds} from '../../utils/getReorderedPresetIds';
import {
  CollapsibleChipItem,
  CollapsibleChipRows,
  RenderVisibleRow,
} from '../CollapsibleChipRows/CollapsibleChipRows';
import {FilterPresetButton, FilterPresetButtonProps} from './components/FilterPresetButton';
import {FilterPresetButtonDragPreview} from './components/FilterPresetButtonDragPreview';
import {SortableFilterPresetButton} from './components/SortableFilterPresetButton';

const PRESET_ROW_LIMIT = 2;

/** Changes whenever a preset chip may change width, so the collapsible row re-measures. */
const getPresetMeasureKey = (
  filterPreset: PresetListItemResponseBody,
  selectedFilterPresetId: string | Nullish
) =>
  `${filterPreset.id}:${filterPreset.name}:${filterPreset.newCount}:${filterPreset.allowNotifications}:${filterPreset.id === selectedFilterPresetId}`;

interface SourcingFilterPresetButtonsProps {
  filterPresetsControl: SourcingFilterPresetsControl;
  isSortingEnabled?: boolean;
  /** Fold the chips to a couple of rows with a "+N more" toggle (behind a feature flag). With
   * sorting on, only the presets on screen can be dragged - expand to reorder the whole list. */
  isCollapsible?: boolean;
}

/** A few px of travel so a tap or a scroll gesture on the handle does not start a drag. */
const DRAG_ACTIVATION_DISTANCE = 8;

export function FilterPresetButtons(props: SourcingFilterPresetButtonsProps) {
  const targetCountry = useSourcingTargetCountry();
  const currency = useSourcingCurrency();
  const sourcingType = useSourcingType();
  const {data, isLoading: arePresetsLoading} = useSourcingGetPresetsListQuery({
    country: targetCountry?.code,
    currency: currency?.code,
  });
  const [reorderPresets] = useSourcingReorderPresetsMutation();
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useLocalStorage(
    sourcingStorageKey.FILTER_PRESETS_EXPANDED,
    false
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {activationConstraint: {distance: DRAG_ACTIVATION_DISTANCE}}),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const orderedPresets = sortBy((a) => a.position, data ?? []);
  const presetIds = orderedPresets.map((preset) => preset.id);
  const draggedPreset = orderedPresets.find((preset) => preset.id === draggedId);

  const getPresetButtonProps = (
    filterPreset: PresetListItemResponseBody
  ): FilterPresetButtonProps => ({
    id: filterPreset.id,
    title: filterPreset.name,
    count: sourcingType === SourcingType.AUCTIONS ? null : filterPreset.newCount,
    onClick: () => props.filterPresetsControl.onSelectFilterPresetId(filterPreset.id),
    isSelected: filterPreset.id === props.filterPresetsControl.selectedFilterPresetId,
    isNotificationsMuted:
      sourcingType === SourcingType.AUCTIONS ? null : !filterPreset.allowNotifications,
    onResetFilterPreset: props.filterPresetsControl.onResetFilterPreset,
  });

  const onDragStart = (event: DragStartEvent) => {
    setDraggedId(event.active.id.toString());
  };

  /** Reorders in the order the user sees - see `getReorderedPresetIds`. */
  const onReorder = (displayedIds: string[]) => (event: DragEndEvent) => {
    const reorderedIds = getReorderedPresetIds(
      displayedIds,
      presetIds,
      event.active.id.toString(),
      event.over?.id.toString(),
      props.filterPresetsControl.selectedFilterPresetId
    );

    if (isNil(reorderedIds)) {
      setDraggedId(null);
      return;
    }

    return reorderPresets({
      reorderPresetsRequestBody: {
        items: reorderedIds.map((id, index) => ({id, position: index + 1})),
      },
    })
      .unwrap()
      .catch(handleApiError)
      .finally(() => setDraggedId(null));
  };

  const isCollapsible =
    (props.isCollapsible ?? false) &&
    isFeatureEnabled(featureFlags.SOURCING_COLLAPSIBLE_FILTER_HEADER);

  /** `sortableIds` must be exactly the sortables on screen - a clipped one has no rect to sort by. */
  const renderSortable: RenderVisibleRow = (sortableIds, row) => (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={onReorder(sortableIds)}
    >
      <SortableContext items={sortableIds} strategy={horizontalListSortingStrategy}>
        {row}
      </SortableContext>
      <DragOverlay>
        <Show when={draggedPreset}>
          {(preset) => <FilterPresetButtonDragPreview {...getPresetButtonProps(preset)} />}
        </Show>
      </DragOverlay>
    </DndContext>
  );

  const renderCollapsibleRow = (
    renderItem: (filterPreset: PresetListItemResponseBody) => CollapsibleChipItem,
    renderVisibleRow?: RenderVisibleRow
  ) => (
    <CollapsibleChipRows
      rowLimit={PRESET_ROW_LIMIT}
      spacing={2}
      isExpanded={isExpanded}
      pinnedId={props.filterPresetsControl.selectedFilterPresetId}
      items={orderedPresets.map(renderItem)}
      renderVisibleRow={renderVisibleRow}
      renderTrailing={(hiddenCount) => (
        <Show when={hiddenCount > 0}>
          <Button
            variant="link"
            title={
              isExpanded
                ? i18n.t('general.actions.hide')
                : i18n.t('entity.sourcing.labels.nMore', {count: hiddenCount})
            }
            onClick={() => setIsExpanded(!isExpanded)}
            data-testid={testIds.sourcing.classifieds('filterPresetButtons-expandToggle')}
          />
        </Show>
      )}
    />
  );

  const selectedFilterPresetId = props.filterPresetsControl.selectedFilterPresetId;

  const presetRow = match({isSortingEnabled: props.isSortingEnabled ?? false, isCollapsible})
    .with({isSortingEnabled: true, isCollapsible: true}, () =>
      renderCollapsibleRow(
        (filterPreset) => ({
          id: filterPreset.id,
          measureKey: getPresetMeasureKey(filterPreset, selectedFilterPresetId),
          node: <SortableFilterPresetButton {...getPresetButtonProps(filterPreset)} />,
        }),
        renderSortable
      )
    )
    .with({isSortingEnabled: true}, () =>
      renderSortable(
        presetIds,
        <HStack grow={1} spacing={2} wrap>
          {orderedPresets.map((filterPreset) => (
            <SortableFilterPresetButton
              key={filterPreset.id}
              {...getPresetButtonProps(filterPreset)}
            />
          ))}
        </HStack>
      )
    )
    .with({isCollapsible: true}, () =>
      renderCollapsibleRow((filterPreset) => ({
        id: filterPreset.id,
        measureKey: getPresetMeasureKey(filterPreset, selectedFilterPresetId),
        node: <FilterPresetButton {...getPresetButtonProps(filterPreset)} />,
      }))
    )
    .otherwise(() => (
      <HStack grow={1} spacing={2} wrap>
        {orderedPresets.map((filterPreset) => (
          <FilterPresetButton key={filterPreset.id} {...getPresetButtonProps(filterPreset)} />
        ))}
      </HStack>
    ));

  return (
    <HStack grow={1} minWidth={0}>
      <SkeletonLoadingBox
        isLoading={arePresetsLoading}
        width={32}
        height={8}
        borderRadius="small"
        contentStyles={{display: 'contents'}}
      >
        {presetRow}
      </SkeletonLoadingBox>
    </HStack>
  );
}
